import {
  Component,
  computed,
  inject,
  input,
  OnInit,
  output,
  signal
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { UmssIconComponent } from '../icon/icon.component';
import { UmssButtonComponent } from '../button/button.component';
import { UmssListItemComponent } from '../list-item/list-item.component';
import { UmssEmptyStateComponent } from '../empty-state/empty-state.component';
import { NewCitationDialogComponent } from '../new-citation-dialog/new-citation-dialog.component';
import { ContractorCitationDetailsDialogComponent } from '../contractor-citation-details-dialog/contractor-citation-details-dialog.component';
import { UmssDialogComponent } from '../dialog/dialog.component';
import { UmssPaginationComponent } from '../pagination/pagination.component';
import {
  ContractorCitationService,
  ContractorCitationListDto,
  ContractorCitationStatus
} from '@umss/shared/data-access';
import { UmssCitationStatusBadgeComponent } from '../citation-status-badge/citation-status-badge.component';
import { CitationsAlertComponent } from '../citations-alert/citations-alert.component';

@Component({
  selector: 'umss-contractor-citations-card',
  standalone: true,
  imports: [
    CommonModule,
    DatePipe,
    UmssIconComponent,
    UmssButtonComponent,
    UmssListItemComponent,
    UmssEmptyStateComponent,
    NewCitationDialogComponent,
    ContractorCitationDetailsDialogComponent,
    UmssDialogComponent,
    UmssPaginationComponent,
    UmssCitationStatusBadgeComponent,
    CitationsAlertComponent
  ],
  templateUrl: './contractor-citations-card.component.html',
  styleUrls: ['./contractor-citations-card.component.scss']
})
export class ContractorCitationsCardComponent implements OnInit {
  readonly contractorId = input.required<string>();
  readonly facilityId = input<string>();
  readonly cardIconTitle = input<string | null>('phosphorReceiptXBold');
  readonly cardTitle = input('Citations');
  readonly cardSubtitle = input(
    `A citation is an official complaint filed by a facility you previously worked with. 
    Receiving three citations triggers an automatic account suspension and a subsequent performance audit`
  );

  readonly showNewButton = input(true);
  readonly dialogMode = input.required<'edit' | 'view'>();
  readonly allowResolve = input(false);

  readonly citationChanged = output<void>();

  private readonly citationService = inject(ContractorCitationService);

  private readonly _effectiveFacilityId = signal<string | undefined>(undefined);

  readonly citations = signal<ContractorCitationListDto[]>([]);
  readonly openedCitations = computed(() =>
    this.citations().filter((c) => c.status === ContractorCitationStatus.Open)
  );
  readonly isLoading = signal(false);
  readonly isNewCitationDialogOpen = signal(false);
  readonly isDetailsDialogOpen = signal(false);
  readonly selectedCitationId = signal<string | null>(null);
  readonly currentPage = signal(1);
  readonly totalPages = signal(1);
  readonly pageSize = 10;

  readonly showPagination = computed(
    () => !(this.currentPage() === 1 && this.citations().length < this.pageSize)
  );

  ngOnInit(): void {
    this._effectiveFacilityId.set(this.facilityId());
    this.loadCitations();
  }

  loadCitations(): void {
    if (!this.contractorId() && !this._effectiveFacilityId()) return;

    this.isLoading.set(true);

    this.citationService
      .getAll({
        contractorId: this.contractorId(),
        facilityId: this._effectiveFacilityId(),
        pageNumber: this.currentPage(),
        pageSize: this.pageSize
      })
      .subscribe({
        next: (response) => {
          this.citations.set(response.items);
          this.totalPages.set(response.totalPages);
          this.isLoading.set(false);
        },
        error: (error) => {
          console.error('Error loading citations:', error);
          this.isLoading.set(false);
        }
      });
  }

  openNewCitationDialog(): void {
    this.isNewCitationDialogOpen.set(true);
  }

  closeNewCitationDialog(): void {
    this.isNewCitationDialogOpen.set(false);
  }

  onNewCitationSaved(): void {
    this.isNewCitationDialogOpen.set(false);
    this.currentPage.set(1);
    this.loadCitations();
    this.citationChanged.emit();
  }

  onCitationDetailsActionClicked(citation: ContractorCitationListDto): void {
    this.selectedCitationId.set(citation.id);
    this.isDetailsDialogOpen.set(true);
  }

  closeDetailsDialog(): void {
    this.isDetailsDialogOpen.set(false);
    this.selectedCitationId.set(null);
  }

  onCitationDetailsSaved(): void {
    this.closeDetailsDialog();
    this.loadCitations();
    this.citationChanged.emit();
  }

  onCitationResolved(): void {
    this.closeDetailsDialog();
    this.loadCitations();
    this.citationChanged.emit();
  }

  onPageChange(page: number): void {
    this.currentPage.set(page);
    this.loadCitations();
  }
}
